import {type FC, useState} from "react";

import type {StepWizardChildProps} from "react-step-wizard";

import {
  ApartmentQuantityField,
  BuildingQuantityField,
  CompanyNameField,
  FloorQuantityField,
  StairwellQuantityField
} from "./FormFields";
import {updatePlan} from "../../actions";
import {useAppDispatch, useAppSelector} from "../../hooks";
import {
  BUILDING_TYPE_BLOCK,
  BUILDING_TYPE_DETACHED,
  BUILDING_TYPE_DUPLEX,
  BUILDING_TYPE_TERRACED,
  type BuildingType,
  type PlanState,
  type YesNo
} from "../../reducers/types";
import type {WizardNavigation} from "../wizard.types";
import {ChoiceFormField} from "./ChoiceFormField";
import {WizardNavigationButtons} from "./WizardNavigationButtons";
import {useWizardNavigation} from "../../utils/app-utils";


export const BuildingDetails: FC<Partial<StepWizardChildProps>> = (props) => {
  const plan = useAppSelector<PlanState>((state) => state.plan)
  const navigation = useWizardNavigation(props)

  return (
    <div>
      {plan.buildingType === BUILDING_TYPE_BLOCK ? <BuildingDetailsBlock navigation={navigation}/> : null}
      {plan.buildingType !== BUILDING_TYPE_BLOCK ? <NonBlockBuildingDetails navigation={navigation}/> : null}
    </div>
  )
}

/* Child Components */

const BuildingDetailsBlock: FC<{ navigation: WizardNavigation }> = ({navigation}) => {
  const plan = useAppSelector<PlanState>((state) => state.plan)
  const dispatch = useAppDispatch()
  const [companyName, setCompanyName] = useState<string>(plan.companyName)
  const [buildingQuantity, setBuildingQuantity] = useState<number | undefined>(plan.buildingQuantity)
  const [stairwellQuantity, setStairwellQuantity] = useState<number | undefined>(plan.stairwellQuantity)
  const [floorQuantity, setFloorQuantity] = useState<number | undefined>(plan.floorQuantity)
  const [apartmentQuantity, setApartmentQuantity] = useState<number | undefined>(plan.apartmentQuantity)

  const isValid = companyName !== '' &&
    (buildingQuantity ?? 0) !== 0 &&
    (stairwellQuantity ?? 0) !== 0 &&
    (floorQuantity ?? 0) !== 0 &&
    (apartmentQuantity ?? 0) !== 0

  const onProceed = () => {
    const updatedPlan: Partial<PlanState> = {
      companyName,
      buildingQuantity,
      stairwellQuantity,
      floorQuantity,
      apartmentQuantity
    }

    dispatch(updatePlan(updatedPlan))
  }

  return (
    <div>
      <h4 className='mb-4'>Taloyhtiön tiedot</h4>
      <div className='row mb-3'>
        <div className='col-12 col-md-8'>
          <CompanyNameField companyName={companyName} setCompanyName={setCompanyName}/>
          <BuildingQuantityField buildingQuantity={buildingQuantity} setBuildingQuantity={setBuildingQuantity}/>
          <StairwellQuantityField stairwellQuantity={stairwellQuantity}
                                  setStairwellQuantity={setStairwellQuantity}/>
          <FloorQuantityField floorQuantity={floorQuantity} setFloorQuantity={setFloorQuantity}/>
          <ApartmentQuantityField apartmentQuantity={apartmentQuantity}
                                  setApartmentQuantity={setApartmentQuantity}/>
        </div>
        <BuildingTypeIcon buildingType={plan.buildingType}/>
      </div>
      <WizardNavigationButtons testIdPrefix='block-details' isValid={isValid} onProceed={onProceed}
                               navigation={navigation}/>
    </div>
  )
}

/**
 * Building details for other apartment types than 'block'.
 */
const NonBlockBuildingDetails: FC<{ navigation: WizardNavigation }> = ({navigation}) => {
  const plan = useAppSelector<PlanState>((state) => state.plan)
  const dispatch = useAppDispatch()
  const [companyName, setCompanyName] = useState<string>(plan.companyName)
  const [buildingQuantity, setBuildingQuantity] = useState<number | undefined>(plan.buildingQuantity)
  const [apartmentQuantity, setApartmentQuantity] = useState<number | undefined>(plan.apartmentQuantity)
  const [centralUnitPlacedInMiddle, setCentralUnitPlacedInMiddle] = useState<'' | YesNo>(plan.centralUnitPlacedInMiddle)

  const isValid = companyName !== '' &&
    (buildingQuantity ?? 0) !== 0 &&
    (apartmentQuantity ?? 0) !== 0 &&
    centralUnitPlacedInMiddle !== ''

  const onProceed = () => {
    const updatedPlan: Partial<PlanState> = {
      companyName,
      buildingQuantity,
      apartmentQuantity,
      centralUnitPlacedInMiddle
    }

    dispatch(updatePlan(updatedPlan))
  }

  const title = getBuildingDetailsTitle(plan.buildingType)
  return (
    <div>
      <h4 className='mb-4'>
        {title}
      </h4>
      <div className='row'>
        <div className='col-12 col-md-8'>
          <CompanyNameField companyName={companyName} setCompanyName={setCompanyName}/>
          <BuildingQuantityField buildingQuantity={buildingQuantity} setBuildingQuantity={setBuildingQuantity}/>
          <ApartmentQuantityField apartmentQuantity={apartmentQuantity} setApartmentQuantity={setApartmentQuantity}/>
          <ChoiceFormField labelText='Saadaanko keskusyksikkö sijoitettua talojen keskelle? *'
                           name='centralUnitPlacedInMiddle'
                           testId='central-unit-placed-in-middle'
                           value={centralUnitPlacedInMiddle}
                           onChange={setCentralUnitPlacedInMiddle}
                           options={[
                             {value: 'yes', display: 'Kyllä'},
                             {value: 'no', display: 'Ei'}
                           ]}
          />
        </div>
        <BuildingTypeIcon buildingType={plan.buildingType}/>
      </div>
      <WizardNavigationButtons testIdPrefix='building-details' isValid={isValid} onProceed={onProceed}
                               navigation={navigation}
      />
    </div>
  )
}

const BuildingTypeIcon: FC<{ buildingType: BuildingType | "" }> = ({buildingType}) => {
  const buildingTypeClass = getBuildingTypeIconClass(buildingType)
  return (
    <div className='d-none d-md-block col-4 text-center'>
      {buildingTypeClass && <i className={`fas ${buildingTypeClass} fa-10x mr-4`} style={{color: '#d2ddec'}}/>}
    </div>
  )
}


/* Helper Functions */

function getBuildingTypeIconClass(buildingType: BuildingType | "") {
  if (!buildingType) {
    return null
  }

  if (buildingType === BUILDING_TYPE_TERRACED) {
    return 'fa-city'
  }

  if (buildingType === BUILDING_TYPE_DUPLEX || buildingType === BUILDING_TYPE_DETACHED) {
    return 'fa-home'
  }

  if (buildingType === BUILDING_TYPE_BLOCK) {
    return 'fa-building'
  }

  return null
}

function getBuildingDetailsTitle(buildingType: BuildingType | ""): string {
  switch (buildingType) {
    case BUILDING_TYPE_TERRACED:
      return 'Rivitalon tiedot'
    case BUILDING_TYPE_DUPLEX:
      return 'Paritalon tiedot'
    case BUILDING_TYPE_DETACHED:
      return 'Erillistalon tiedot'

    default:
      return 'Taloyhtiön tiedot'

  }
}
